import React, { useState, useEffect, useRef } from 'react';
import MaterialTable from 'material-table';
import { useSelector, useDispatch } from 'react-redux';
import CircularLoading from '../../components/CircularLoading';
import CountrySelect from '../../components/CountrySelect';
import { api } from 'common';
import { makeStyles } from '@mui/styles';
import {
  Modal,
  Grid,
  Typography,
  Switch,
  Autocomplete,
  TextField,
  CircularProgress,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useTranslation } from 'react-i18next';
import Button from 'components/CustomButtons/Button.js';
import { colors } from '../../components/Theme/WebTheme';
import Swal from 'sweetalert2';
import {
  getEnabledCityOptions,
  resolveCanonicalCity,
} from '../../common/sharedFunctions';

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    width: 450,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    borderRadius: 15,
  },
}));

const flagUrl = code => `https://flagcdn.com/w40/${code.toLowerCase()}.png`;
// Large countries (USA, India, etc.) can have 15,000+ cities in the dataset -
// cap rendered matches so the dropdown never has to mount thousands of DOM nodes.
const cityFilterOptions = createFilterOptions({ limit: 50 });

export default function CountriesPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const classes = useStyles();
  const rootRef = useRef(null);
  const dispatch = useDispatch();

  const settings = useSelector(state => state.settingsdata.settings);
  const countriesdata = useSelector(state => state.countriesdata);
  const usersList = useSelector(state => state.usersdata.users);
  const carsList = useSelector(state => state.carlistdata.cars);
  const carTypesList = useSelector(state => state.cartypes.cars);
  const {
    fetchCountries,
    editCountry,
    editCity,
    fetchCountryDialCodes,
    fetchCitiesForCountry,
    bulkUpdateCityFields,
  } = api;

  const [normalizing, setNormalizing] = useState(false);

  const [data, setData] = useState([]);
  const [dialCodes, setDialCodes] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [newCountryOption, setNewCountryOption] = useState(null);

  const [citiesOpen, setCitiesOpen] = useState(false);
  const [activeCode, setActiveCode] = useState(null);
  const activeCountry = data.find(c => c.code === activeCode) || null;

  const [addCityOpen, setAddCityOpen] = useState(false);
  const [cityNameInput, setCityNameInput] = useState('');
  const [selectedCitySuggestion, setSelectedCitySuggestion] = useState(null);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [citySuggestionsCountry, setCitySuggestionsCountry] = useState(null);
  const [citySuggestionsLoading, setCitySuggestionsLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch, fetchCountries]);

  useEffect(() => {
    fetchCountryDialCodes()
      .then(list => setDialCodes(list))
      .catch(() => setDialCodes([]));
  }, [fetchCountryDialCodes]);

  useEffect(() => {
    setData(countriesdata.countries ? countriesdata.countries : []);
  }, [countriesdata.countries]);

  const availableCountries = api.countries.filter(
    c => !data.some(d => d.code === c.code),
  );

  const reportWriteError = error => {
    alert(
      'No se pudo guardar en Firebase: ' +
        (error && error.message ? error.message : error) +
        '\n\nSi el mensaje dice "permission_denied", falta desplegar las reglas de la base de datos (json/database-rules.json) al proyecto de Firebase.',
    );
  };

  const toggleCountryEnabled = country => {
    if (!settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode'));
      return;
    }
    dispatch(
      editCountry({ ...country, enabled: !country.enabled }, 'Update'),
    ).catch(reportWriteError);
  };

  const toggleCityEnabled = (countryCode, city) => {
    if (!settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode'));
      return;
    }
    dispatch(
      editCity(countryCode, { ...city, enabled: !city.enabled }, 'Update'),
    ).catch(reportWriteError);
  };

  const handleAddCountry = () => {
    if (!settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode'));
      return;
    }
    if (!newCountryOption) {
      alert('Selecciona un país');
      return;
    }
    const match = dialCodes.find(d => d.code === newCountryOption.code);
    const dialCode = match ? match.dial_code : `+${newCountryOption.phone}`;
    dispatch(
      editCountry(
        {
          code: newCountryOption.code,
          name: newCountryOption.label,
          dialCode,
          enabled: true,
          createdAt: new Date().getTime(),
        },
        'Add',
      ),
    ).catch(reportWriteError);
    setNewCountryOption(null);
    setAddOpen(false);
  };

  const openAddCity = () => {
    setCityNameInput('');
    setSelectedCitySuggestion(null);
    setAddCityOpen(true);
    if (citySuggestionsCountry !== activeCountry.code) {
      setCitySuggestionsLoading(true);
      fetchCitiesForCountry(activeCountry.code)
        .then(list => {
          setCitySuggestions(list);
          setCitySuggestionsCountry(activeCountry.code);
        })
        .catch(() => {
          setCitySuggestions([]);
          setCitySuggestionsCountry(activeCountry.code);
        })
        .finally(() => setCitySuggestionsLoading(false));
    }
  };

  const handleAddCity = () => {
    if (!settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode'));
      return;
    }
    const name = cityNameInput.trim();
    if (!name) {
      alert('Escribe o busca el nombre de la ciudad');
      return;
    }
    // Only trust the suggestion's coordinates if the text still matches it -
    // if they picked a suggestion and then kept typing, treat it as free text.
    const city =
      selectedCitySuggestion && selectedCitySuggestion.name === name
        ? {
            name,
            state: selectedCitySuggestion.state,
            latitude: selectedCitySuggestion.latitude,
            longitude: selectedCitySuggestion.longitude,
            enabled: true,
          }
        : { name, enabled: true };
    dispatch(editCity(activeCountry.code, city, 'Add')).catch(
      reportWriteError,
    );
    setCityNameInput('');
    setSelectedCitySuggestion(null);
    setAddCityOpen(false);
  };

  // --- Normalización masiva de ciudades ------------------------------------
  // Recorre users/cars/cartypes y calcula el identificador canónico de cada
  // ciudad (MAYÚSCULAS, sin acentos, sin espacios, "CIUDAD-DEPARTAMENTO").
  const buildCityChanges = () => {
    const cityOptions = getEnabledCityOptions(countriesdata);
    const changes = [];
    const samples = [];

    const consider = (path, current) => {
      const target = resolveCanonicalCity(current, cityOptions);
      if (target && String(current || '') !== target) {
        changes.push({ path, value: target });
        if (samples.length < 25) {
          samples.push({
            entity: path.split('/')[0],
            from: current || '(vacío)',
            to: target,
          });
        }
      }
    };

    (usersList || []).forEach(u => {
      if (u && u.id) consider(`users/${u.id}/city`, u.city);
    });
    (carsList || []).forEach(c => {
      if (c && c.id) consider(`cars/${c.id}/vehicleCity`, c.vehicleCity);
    });
    (carTypesList || []).forEach(ct => {
      if (ct && ct.id) consider(`cartypes/${ct.id}/city`, ct.city);
    });

    return { changes, samples };
  };

  const normalizeAllCityRecords = async () => {
    if (!settings.AllowCriticalEditsAdmin) {
      alert(t('demo_mode'));
      return;
    }
    const { changes, samples } = buildCityChanges();
    if (changes.length === 0) {
      Swal.fire({
        icon: 'info',
        title: 'Todo en orden',
        text: 'No hay ciudades por normalizar.',
      });
      return;
    }
    const rows = samples
      .map(s => `${s.entity}:  "${s.from}"  →  "${s.to}"`)
      .join('\n');
    const more =
      changes.length > samples.length
        ? `\n… y ${changes.length - samples.length} más`
        : '';
    const result = await Swal.fire({
      icon: 'warning',
      title: `Normalizar ${changes.length} registro(s)`,
      html: `<pre style="text-align:left;max-height:260px;overflow:auto;font-size:12px;white-space:pre-wrap">${rows}${more}</pre>`,
      showCancelButton: true,
      confirmButtonText: 'Aplicar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;
    setNormalizing(true);
    try {
      const written = await dispatch(bulkUpdateCityFields(changes));
      Swal.fire({
        icon: 'success',
        title: 'Listo',
        text: `${written} registro(s) normalizado(s). Recarga las pantallas para ver los cambios.`,
      });
    } catch (error) {
      reportWriteError(error);
    } finally {
      setNormalizing(false);
    }
  };

  const columns = [
    {
      title: '',
      field: 'flag',
      sorting: false,
      editable: 'never',
      cellStyle: { width: 40 },
      render: rowData => (
        <img
          loading="lazy"
          width="24"
          src={flagUrl(rowData.code)}
          alt={rowData.code}
        />
      ),
    },
    { title: 'País', field: 'name', editable: 'never' },
    { title: 'Código', field: 'code', editable: 'never' },
    { title: 'Indicativo', field: 'dialCode', editable: 'never' },
    {
      title: 'Ciudades habilitadas',
      field: 'citiesCount',
      sorting: false,
      editable: 'never',
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
      render: rowData => {
        const cities = rowData.cities ? Object.values(rowData.cities) : [];
        const enabledCount = cities.filter(c => c.enabled).length;
        return `${enabledCount}/${cities.length}`;
      },
    },
    {
      title: 'Habilitado',
      field: 'enabled',
      sorting: false,
      editable: 'never',
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
      render: rowData => (
        <Switch
          checked={!!rowData.enabled}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            e.stopPropagation();
            toggleCountryEnabled(rowData);
          }}
          color="primary"
        />
      ),
    },
  ];

  const cityColumns = [
    { title: 'Ciudad', field: 'name' },
    {
      title: 'Estado/Departamento',
      field: 'state',
      editable: 'never',
      sorting: false,
      render: rowData => rowData.state || '—',
    },
    {
      title: 'Coordenadas',
      field: 'coordinates',
      editable: 'never',
      sorting: false,
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
      render: rowData =>
        typeof rowData.latitude === 'number' &&
        typeof rowData.longitude === 'number'
          ? `${rowData.latitude.toFixed(4)}, ${rowData.longitude.toFixed(4)}`
          : '—',
    },
    {
      title: 'Habilitada',
      field: 'enabled',
      sorting: false,
      editable: 'never',
      cellStyle: { textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
      render: rowData => (
        <Switch
          checked={!!rowData.enabled}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            e.stopPropagation();
            toggleCityEnabled(activeCountry.code, rowData);
          }}
          color="primary"
        />
      ),
    },
  ];

  const cityRows = activeCountry && activeCountry.cities
    ? Object.keys(activeCountry.cities).map(key => ({
        ...activeCountry.cities[key],
        id: key,
      }))
    : [];

  const tableLocalization = {
    body: {
      addTooltip: t('add'),
      deleteTooltip: t('delete'),
      editTooltip: t('edit'),
      emptyDataSourceMessage: t('blank_message'),
      editRow: {
        deleteText: t('delete_message'),
        cancelTooltip: t('cancel'),
        saveTooltip: t('save'),
      },
    },
    toolbar: {
      searchPlaceholder: t('search'),
      exportTitle: t('export'),
    },
    header: {
      actions: t('actions'),
    },
    pagination: {
      labelDisplayedRows: '{from}-{to} ' + t('of') + ' {count}',
      firstTooltip: t('first_page_tooltip'),
      previousTooltip: t('previous_page_tooltip'),
      nextTooltip: t('next_page_tooltip'),
      lastTooltip: t('last_page_tooltip'),
    },
  };

  const tableHeaderOptions = {
    rowStyle: rowData => ({
      backgroundColor:
        selectedRow === rowData.tableData.id ? '#EEE' : '#FFF',
    }),
    editable: {
      backgroundColor: colors.Header_Text,
      fontSize: '0.8em',
      fontWeight: 'bold ',
      fontFamily: 'Lucida Console", "Courier New", monospace',
    },
    headerStyle: {
      position: 'sticky',
      top: '0px',
      backgroundColor: colors.Header_Text_back,
      color: '#fff',
      fontSize: '0.8em',
      fontWeight: 'bold ',
      fontFamily: 'Lucida Console", "Courier New", monospace',
    },
  };

  return countriesdata.loading ? (
    <CircularLoading />
  ) : (
    <div ref={rootRef}>
      {countriesdata.error && countriesdata.error.flag ? (
        <Typography
          style={{ color: '#c62828', padding: '10px 0', fontWeight: 'bold' }}
        >
          No se pudo leer "countries" de Firebase ({countriesdata.error.msg}
          ). Si el error es "permission_denied", faltan desplegar las reglas
          de json/database-rules.json.
        </Typography>
      ) : null}

      <Grid
        container
        alignItems="center"
        spacing={2}
        style={{ marginBottom: 12 }}
      >
        <Grid item>
          <Button
            onClick={normalizeAllCityRecords}
            variant="contained"
            color="secondaryButton"
            disabled={normalizing}
          >
            <AutoFixHighIcon style={{ marginRight: 6 }} />
            {normalizing
              ? 'Normalizando…'
              : 'Normalizar ciudades de todos los registros'}
          </Button>
        </Grid>
        <Grid item xs>
          <Typography variant="body2" style={{ color: '#666' }}>
            Reescribe <code>users.city</code>, <code>cars.vehicleCity</code> y{' '}
            <code>cartypes.city</code> a la forma canónica (MAYÚSCULAS, sin
            acentos ni espacios, p. ej. <code>MEDELLIN-ANTIOQUIA</code>). Muestra
            una vista previa antes de aplicar.
          </Typography>
        </Grid>
      </Grid>

      <MaterialTable
        title="Países y Ciudades"
        columns={columns}
        style={{
          direction: isRTL === 'rtl' ? 'rtl' : 'ltr',
          borderRadius: '8px',
          boxShadow: '4px 4px 6px #9E9E9E',
        }}
        data={data}
        onRowClick={(evt, selectedRow) =>
          setSelectedRow(selectedRow.tableData.id)
        }
        options={{
          exportButton: true,
          pageSize: 10,
          ...tableHeaderOptions,
        }}
        localization={tableLocalization}
        editable={{
          onRowDelete: oldData =>
            settings.AllowCriticalEditsAdmin
              ? new Promise(resolve => {
                  setTimeout(() => {
                    resolve();
                    dispatch(editCountry(oldData, 'Delete')).catch(
                      reportWriteError,
                    );
                  }, 600);
                })
              : new Promise(resolve => {
                  setTimeout(() => {
                    resolve();
                    alert(t('demo_mode'));
                  }, 600);
                }),
        }}
        actions={[
          {
            icon: () => (
              <div
                style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <AddIcon />
                <Typography variant="subtitle2" style={{ padding: 5 }}>
                  Agregar país
                </Typography>
              </div>
            ),
            isFreeAction: true,
            onClick: () => setAddOpen(true),
          },
          rowData => ({
            icon: () => (
              <div
                style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <LocationCityIcon />
                <Typography variant="subtitle2" style={{ padding: 5 }}>
                  Ciudades
                </Typography>
              </div>
            ),
            onClick: (event, rowData) => {
              setActiveCode(rowData.code);
              setCitiesOpen(true);
            },
          }),
        ]}
      />

      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={addOpen}
        onClose={() => setAddOpen(false)}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Grid
          container
          spacing={2}
          className={classes.paper}
          style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
        >
          <Grid item xs={12}>
            <Typography component="h1" variant="h6">
              Agregar país
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <CountrySelect
              countries={availableCountries}
              label="País"
              value={newCountryOption}
              onChange={(event, value) => setNewCountryOption(value)}
            />
          </Grid>
          <Grid item xs={12} style={{ textAlign: isRTL === 'rtl' ? 'left' : 'right' }}>
            <Button
              onClick={() => setAddOpen(false)}
              variant="contained"
              color="danger"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleAddCountry}
              variant="contained"
              color="secondaryButton"
              style={{ marginLeft: 10 }}
            >
              {t('save')}
            </Button>
          </Grid>
        </Grid>
      </Modal>

      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={citiesOpen}
        onClose={() => setCitiesOpen(false)}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <div className={classes.paper} style={{ width: 750 }}>
          {activeCountry ? (
            <MaterialTable
              title={`Ciudades de ${activeCountry.name}`}
              columns={cityColumns}
              data={cityRows}
              style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
              options={{ exportButton: false, pageSize: 5, ...tableHeaderOptions }}
              localization={tableLocalization}
              actions={[
                {
                  icon: () => (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                      }}
                    >
                      <AddIcon />
                      <Typography variant="subtitle2" style={{ padding: 5 }}>
                        Agregar ciudad
                      </Typography>
                    </div>
                  ),
                  isFreeAction: true,
                  onClick: openAddCity,
                },
              ]}
              editable={{
                onRowUpdate: (newData, oldData) =>
                  settings.AllowCriticalEditsAdmin
                    ? new Promise((resolve, reject) => {
                        setTimeout(() => {
                          if (!(newData && newData.name)) {
                            alert('Ingresa el nombre de la ciudad');
                            reject();
                          } else {
                            resolve();
                            if (newData !== oldData) {
                              delete newData.tableData;
                              dispatch(
                                editCity(activeCountry.code, newData, 'Update'),
                              ).catch(reportWriteError);
                            }
                          }
                        }, 400);
                      })
                    : new Promise(resolve => {
                        setTimeout(() => {
                          resolve();
                          alert(t('demo_mode'));
                        }, 400);
                      }),
                onRowDelete: oldData =>
                  settings.AllowCriticalEditsAdmin
                    ? new Promise(resolve => {
                        setTimeout(() => {
                          resolve();
                          dispatch(
                            editCity(activeCountry.code, oldData, 'Delete'),
                          ).catch(reportWriteError);
                        }, 400);
                      })
                    : new Promise(resolve => {
                        setTimeout(() => {
                          resolve();
                          alert(t('demo_mode'));
                        }, 400);
                      }),
              }}
            />
          ) : null}
        </div>
      </Modal>

      <Modal
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        open={addCityOpen}
        onClose={() => setAddCityOpen(false)}
        className={classes.modal}
        container={() => rootRef.current}
      >
        <Grid
          container
          spacing={2}
          className={classes.paper}
          style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
        >
          <Grid item xs={12}>
            <Typography component="h1" variant="h6">
              Agregar ciudad {activeCountry ? `(${activeCountry.name})` : ''}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              autoHighlight
              loading={citySuggestionsLoading}
              options={citySuggestions}
              filterOptions={cityFilterOptions}
              inputValue={cityNameInput}
              onInputChange={(event, newInputValue) => {
                setCityNameInput(newInputValue);
                if (
                  !selectedCitySuggestion ||
                  selectedCitySuggestion.name !== newInputValue
                ) {
                  setSelectedCitySuggestion(null);
                }
              }}
              onChange={(event, value) => {
                if (value && typeof value !== 'string') {
                  setSelectedCitySuggestion(value);
                  setCityNameInput(value.name);
                }
              }}
              getOptionLabel={option =>
                typeof option === 'string'
                  ? option
                  : `${option.name}${option.state ? ' — ' + option.state : ''}`
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Ciudad"
                  placeholder="Busca una ciudad..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {citySuggestionsLoading ? (
                          <CircularProgress color="inherit" size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" style={{ color: '#666' }}>
              {citySuggestionsLoading
                ? 'Cargando ciudades conocidas de este país...'
                : selectedCitySuggestion &&
                  selectedCitySuggestion.name === cityNameInput
                ? `Coordenadas: ${selectedCitySuggestion.latitude}, ${selectedCitySuggestion.longitude}`
                : 'Selecciona una sugerencia para guardar sus coordenadas, o escribe un nombre propio si no aparece en la lista.'}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            style={{ textAlign: isRTL === 'rtl' ? 'left' : 'right' }}
          >
            <Button
              onClick={() => setAddCityOpen(false)}
              variant="contained"
              color="danger"
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleAddCity}
              variant="contained"
              color="secondaryButton"
              style={{ marginLeft: 10 }}
            >
              {t('save')}
            </Button>
          </Grid>
        </Grid>
      </Modal>
    </div>
  );
}
